import {Dropzone} from '@coolcolduk/ui-mui';
import DeleteIcon from '@mui/icons-material/Delete';
import {Box, Button, Card, CardContent, Typography} from '@mui/material';
import {useState} from 'react';
import {UserSoundType} from '@cp/model';

export interface GeneralSettingsSoundFieldProps {
  soundType: UserSoundType;
  soundUrl?: string | null;
  onFileSelect: (soundType: UserSoundType, file: File | null) => void;
  loading: boolean;
}

export default function GeneralSettingsSoundField({
  soundType,
  soundUrl,
  onFileSelect,
  loading,
}: GeneralSettingsSoundFieldProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (files: File[]) => {
    const file = files[0] || null;
    setSelectedFile(file);
    onFileSelect(soundType, file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    onFileSelect(soundType, null);
  };

  const getSoundTypeLabel = (type: UserSoundType): string => {
    switch (type) {
      case UserSoundType.CLICK:
        return 'Click Sound';
      case UserSoundType.CHECK:
        return 'Check Sound';
      case UserSoundType.DROPDOWN:
        return 'Dropdown Sound';
      case UserSoundType.SWITCH:
        return 'Switch Sound';
      case UserSoundType.SLIDE:
        return 'Slide Sound';
      default:
        return 'Sound';
    }
  };

  return (
    <Card sx={{mb: 1.5, maxWidth: '220px'}}>
      <CardContent sx={{py: 2}}>
        <Typography variant="subtitle1" sx={{mb: 1.5}}>
          {getSoundTypeLabel(soundType)}
        </Typography>

        {soundUrl && (
          <Box sx={{mb: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1}}>
            <Typography variant="body2" color="text.secondary">
              Current: {soundUrl.split('/').pop() || 'Custom sound'}
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemoveFile}
              disabled={loading}
              sx={{mt: 0.5}}
            >
              Remove
            </Button>
          </Box>
        )}

        <Box sx={{mb: 1}}>
          <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
            Drag & drop audio file or click to select
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
            MP3, WAV, OGG • Max 5MB
          </Typography>
          <Dropzone setFiles={handleFileSelect} />
        </Box>

        {selectedFile && (
          <Box sx={{mt: 1, p: 1.5, bgcolor: 'success.50', borderRadius: 1}}>
            <Typography variant="body2" color="success.dark">
              Selected: {selectedFile.name}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
